import type { CoverageRule } from '@/shared/types';
import { calculateExactLeaseMonths } from './leaseDurationMonths.util';

/**
 * SMB configured tier defaults — always 3 / 6 / 12 months (confirmed by product).
 * Maps each screening status id to its configured Lease Guarantee coverage tier.
 */
export const SMB_CONFIGURED_TIER_MONTHS: Record<string, number> = {
  approved: 3,
  conditional: 6,
  denied: 12,
};

// Tolerates dayjs day-based interpolation drift (e.g. 2.9999... for an exact
// 3-month lease) so the boundary check doesn't accidentally drop a tier.
const LEASE_DURATION_EPSILON = 0.01;

/**
 * Filters SMB coverage rules by lease duration. A rule is kept only when its
 * configured tier (3 / 6 / 12) is less than or equal to the lease duration,
 * so the dropdown never shows a tier the user can't qualify for. Unknown
 * rule ids are dropped — SMB only has approved / conditional / denied.
 *
 * @param rules - Coverage rules returned by DDS
 * @param leaseStart - The lease start date
 * @param leaseEnd - The lease end date
 * @returns The subset of rules whose configured tier fits within the lease duration.
 */
export const filterSmbRulesByLeaseDuration = (
  rules: CoverageRule[],
  leaseStart?: Date,
  leaseEnd?: Date,
): CoverageRule[] => {
  const leaseDurationMonths = calculateExactLeaseMonths(leaseStart, leaseEnd);
  return rules.filter((rule) => {
    const tier = SMB_CONFIGURED_TIER_MONTHS[rule.id];
    if (tier === undefined) {
      return false;
    }
    return tier <= leaseDurationMonths + LEASE_DURATION_EPSILON;
  });
};
